/**
 * The More sheet — every category the grid has no room for, searchable, in
 * the user's order. Picking one selects it and closes the sheet; it then
 * stands in for the grid's last slot (`gridLayout`).
 */

import { useState } from "react";

import { CategoryGlyph } from "@/features/common/category_glyph";
import { RuleList, RuleRow } from "@/features/common/rule_list";
import { Sheet } from "@/features/common/sheet";
import type { Category } from "@/lib/types";

export function MoreSheet({
  categories,
  familyName,
  familyIds,
  selectedId,
  onPick,
  onClose,
}: {
  categories: readonly Category[];
  familyName?: string;
  familyIds?: ReadonlySet<string>;
  selectedId: string | null;
  onPick: (id: string) => void;
  onClose: () => void;
}): React.JSX.Element {
  const [query, setQuery] = useState("");
  const needle = query.trim().toLocaleLowerCase();
  const matches = needle === "" ? categories : categories.filter((c) => c.name.toLocaleLowerCase().includes(needle));

  return (
    <Sheet title="All categories" onClose={onClose}>
      <div className="px-phi-1 pb-phi-n2">
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Find a category"
          aria-label="Find a category"
          className="min-h-[44px] w-full rounded-phi-n4 border border-line bg-bg-2 px-phi-n1 text-phi-0 text-tx placeholder:text-tx-2"
        />
      </div>
      {matches.length === 0 ? (
        <p className="px-phi-1 py-phi-0 text-phi-n1 text-tx-2">No category called “{query.trim()}”.</p>
      ) : (
        <>
          {(familyName ? [{ label: "Yours", rows: matches.filter(c => !familyIds?.has(c.id)) }, { label: `👪 ${familyName}`, rows: matches.filter(c => familyIds?.has(c.id)) }] : [{ label: "Categories", rows: matches }]).filter(section => section.rows.length > 0).map(section => <div key={section.label}>
            {familyName ? <h3 className="px-phi-1 py-phi-n1 text-phi-n1 font-semibold text-tx-2">{section.label}</h3> : null}
            <RuleList label={section.label}>{section.rows.map(category => <RuleRow key={category.id} leading={<CategoryGlyph category={category} />} title={`${category.name}${familyIds?.has(category.id) ? " · 👪" : ""}`} selected={category.id === selectedId} onClick={() => onPick(category.id)} />)}</RuleList>
          </div>)}
        </>
      )}
    </Sheet>
  );
}
